/**
 * 무전기(PTT) 방식 음성 녹음
 *
 * 실시간 통역(useVoiceRealtimeTranslate)은 WebSocket 으로 PCM 을 계속 흘려보내지만,
 * AI 음성 작성은 "말 한 번 → 분석 한 번" 이므로 MediaRecorder 로 한 덩어리를 녹음해
 * 끝났을 때 Blob 하나를 돌려준다(Whisper 로 전송).
 *
 * 사용:
 *   const recorder = useVoiceRecorder({ maxMs: 60000, onLevel, onAutoStop })
 *   await recorder.start()
 *   const blob = await recorder.stop()   // 취소 시 null
 */

// iOS Safari 는 webm 을 지원하지 않아 mp4(aac)로 떨어진다.
const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
]

function pickMimeType() {
  if (typeof MediaRecorder === 'undefined') return ''
  for (let i = 0; i < MIME_CANDIDATES.length; i += 1) {
    if (MediaRecorder.isTypeSupported(MIME_CANDIDATES[i])) return MIME_CANDIDATES[i]
  }
  return ''
}

function extensionOf(mimeType) {
  if (!mimeType) return 'webm'
  if (mimeType.indexOf('mp4') >= 0) return 'm4a'
  if (mimeType.indexOf('ogg') >= 0) return 'ogg'
  return 'webm'
}

export function isRecordingSupported() {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && typeof MediaRecorder !== 'undefined')
}

/**
 * 입력(마이크) 장치 목록. 라벨은 마이크 권한을 한 번 허용한 뒤에만 채워진다.
 * 마이크가 여러 개인 PC 에서 엉뚱한 장치가 잡혀 무음이 녹음되는 경우를 위해 쓴다.
 */
export async function listAudioInputs() {
  if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return []
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    return devices
      .filter(function (device) { return device.kind === 'audioinput' })
      .map(function (device) { return { deviceId: device.deviceId, label: device.label } })
  } catch (e) {
    return []
  }
}

export function useVoiceRecorder(options) {
  const opts = options || {}
  const maxMs = opts.maxMs || 60000       // 비용 보호 — 한 번에 최대 녹음 시간
  const minMs = opts.minMs || 400         // 너무 짧은 클릭은 무시

  let stream = null
  let recorder = null
  let chunks = []
  let mimeType = ''
  let startedAt = 0
  let maxTimer = null
  let cancelled = false

  // 입력 레벨 표시용
  let audioCtx = null
  let analyser = null
  let levelTimer = null
  let peakLevel = 0        // 녹음하는 동안 관측한 최대 입력 레벨
  let levelAvailable = false // 레벨 측정 자체가 동작했는지(무음 판정 신뢰도)

  async function _startLevelMeter() {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext
      if (!Ctx) return
      audioCtx = new Ctx()
      // 사용자 제스처 밖(getUserMedia await 이후)에서 만들면 suspended 로 시작할 수
      // 있다. 그대로 두면 레벨이 늘 0 이라 정상 녹음도 무음으로 오판한다.
      if (audioCtx.state === 'suspended') {
        try { await audioCtx.resume() } catch (e) { /* ignore */ }
      }

      const source = audioCtx.createMediaStreamSource(stream)
      analyser = audioCtx.createAnalyser()
      analyser.fftSize = 512
      source.connect(analyser)

      const buffer = new Uint8Array(analyser.frequencyBinCount)
      levelTimer = setInterval(function () {
        analyser.getByteTimeDomainData(buffer)
        let sum = 0
        for (let i = 0; i < buffer.length; i += 1) {
          const v = (buffer[i] - 128) / 128
          sum += v * v
        }
        const level = Math.min(1, Math.sqrt(sum / buffer.length) * 4)
        if (audioCtx && audioCtx.state === 'running') levelAvailable = true
        if (level > peakLevel) peakLevel = level
        if (opts.onLevel) opts.onLevel(level)
      }, 100)
    } catch (e) {
      // 레벨 미터는 부가 기능이라 실패해도 녹음은 계속한다.
    }
  }

  function _stopLevelMeter() {
    if (levelTimer) { clearInterval(levelTimer); levelTimer = null }
    if (audioCtx) {
      try { audioCtx.close() } catch (e) { /* ignore */ }
      audioCtx = null
    }
    analyser = null
    if (opts.onLevel) opts.onLevel(0)
  }

  function _release() {
    _stopLevelMeter()
    if (maxTimer) { clearTimeout(maxTimer); maxTimer = null }
    if (stream) {
      stream.getTracks().forEach(function (track) { track.stop() })
      stream = null
    }
    recorder = null
  }

  async function start() {
    if (recorder) return
    if (!isRecordingSupported()) {
      throw new Error('이 브라우저에서는 음성 녹음을 사용할 수 없습니다')
    }

    cancelled = false
    chunks = []
    peakLevel = 0
    levelAvailable = false

    const audioConstraints = { channelCount: 1, echoCancellation: true, noiseSuppression: true }
    // 입력 장치를 직접 고른 경우(마이크가 여러 개인 PC) 그 장치로 녹음한다.
    if (opts.deviceId) audioConstraints.deviceId = { exact: opts.deviceId }

    stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints })

    mimeType = pickMimeType()
    recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)
    recorder.ondataavailable = function (event) {
      if (event.data && event.data.size > 0) chunks.push(event.data)
    }
    recorder.start()
    startedAt = Date.now()

    _startLevelMeter()

    maxTimer = setTimeout(function () {
      if (opts.onAutoStop) opts.onAutoStop('max_duration')
    }, maxMs)
  }

  /**
   * @returns {Promise<{blob, filename, durationMs, peakLevel, levelAvailable}|null>}
   *   너무 짧거나 취소한 경우 null
   *
   *   peakLevel: 녹음 중 관측한 최대 입력 레벨(0~1). 무음 판정에 쓴다.
   *   levelAvailable: 레벨 측정이 실제로 동작했는지. false 면 peakLevel 을 믿을 수 없다.
   */
  function stop() {
    return new Promise(function (resolve) {
      if (!recorder) { resolve(null); return }

      const durationMs = Date.now() - startedAt
      const activeRecorder = recorder
      const measuredPeak = peakLevel
      const measured = levelAvailable

      activeRecorder.onstop = function () {
        const blob = new Blob(chunks, { type: mimeType || 'audio/webm' })
        chunks = []
        _release()

        if (cancelled || durationMs < minMs || blob.size === 0) {
          resolve(null)
          return
        }
        resolve({
          blob,
          filename: 'record.' + extensionOf(mimeType),
          durationMs,
          peakLevel: measuredPeak,
          levelAvailable: measured,
        })
      }

      try {
        activeRecorder.stop()
      } catch (e) {
        _release()
        resolve(null)
      }
    })
  }

  function cancel() {
    cancelled = true
    return stop()
  }

  function isRecording() {
    return !!recorder
  }

  return { start, stop, cancel, isRecording }
}
